import { IconButton } from '@lifeomic/chroma-react/components/IconButton';
import { Tooltip } from '@lifeomic/chroma-react/components/Tooltip';
import { makeStyles } from '@lifeomic/chroma-react/styles';
import { Edit } from '@lifeomic/chromicons';
import React, { useMemo } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { useHistory } from 'react-router-dom';
import { usePolicy } from '../../../../hooks';
import { enforce } from '../../../../util/abac';
import { buildZoriCodingsReviewUrl } from '../../../../util/zoriCodingsReviewUrl';
import type { MedicalResultHistory } from './hooks/useFetchMedicalResultHistories';

const messages = defineMessages({
  reviewCoding: {
    id: 'labResults.reviewCodeMapping.reviewCoding',
    defaultMessage: 'Review coding',
  },
  contactMedicalDirector: {
    id: 'labResults.reviewCodeMapping.contactMedicalDirector',
    defaultMessage:
      'Reach out to Medical Director to have this coding reviewed and categorized',
  },
});

const useStyles = makeStyles(theme => ({
  root: {
    display: 'flex',
    alignItems: 'center',
    flexShrink: 0,
  },
  disabledWrap: {
    display: 'inline-flex',
  },
  button: {
    color:
      theme.palette.mode === 'dark'
        ? theme.palette.text.secondary
        : theme.palette.black[500],
  },
}));

export type ReviewCodeMappingCoding = {
  code?: string;
  system?: string;
  display?: string;
};

export function getCodingForReview(
  medicalResultHistory: MedicalResultHistory,
): ReviewCodeMappingCoding | null {
  const fromHistory = medicalResultHistory.medicalResultHistory[0]?.coding;
  if (fromHistory?.code || fromHistory?.display) {
    return {
      code: fromHistory.code,
      system: fromHistory.system,
      display: fromHistory.display,
    };
  }

  const dimensionCode = medicalResultHistory.dimension.codes[0];
  if (!dimensionCode) {
    return null;
  }

  return {
    code: dimensionCode.key,
    system: dimensionCode.system,
    display: dimensionCode.display,
  };
}

/** Matches medical-results-service create/update/delete (`codingsAdmin` only). */
export function canManageCodeMappings(
  policy: ReturnType<typeof usePolicy>,
): boolean {
  return enforce('codingsAdmin', policy);
}

type ReviewCodeMappingActionProps = {
  medicalResultHistory: MedicalResultHistory;
};

export function ReviewCodeMappingAction({
  medicalResultHistory,
}: ReviewCodeMappingActionProps) {
  const classes = useStyles();
  const { formatMessage } = useIntl();
  const history = useHistory();
  const policy = usePolicy();

  const coding = useMemo(
    () => getCodingForReview(medicalResultHistory),
    [medicalResultHistory],
  );
  const reviewUrl = useMemo(
    () => (coding ? buildZoriCodingsReviewUrl(coding) : null),
    [coding],
  );

  const canManage = canManageCodeMappings(policy);

  if (!reviewUrl) {
    return null;
  }

  const label = formatMessage(messages.reviewCoding);

  const stopRowClick = (event: React.MouseEvent) => {
    event.stopPropagation();
  };

  if (canManage) {
    return (
      <div className={classes.root} data-testid="review-code-mapping-action">
        <Tooltip title={label}>
          <IconButton
            className={classes.button}
            aria-label={label}
            icon={Edit}
            size={0}
            onClick={event => {
              stopRowClick(event);
              history.push(reviewUrl);
            }}
          />
        </Tooltip>
      </div>
    );
  }

  return (
    <div className={classes.root} data-testid="review-code-mapping-action">
      <Tooltip title={formatMessage(messages.contactMedicalDirector)}>
        <span className={classes.disabledWrap}>
          <IconButton
            className={classes.button}
            aria-label={label}
            icon={Edit}
            size={0}
            disabled
            onClick={stopRowClick}
          />
        </span>
      </Tooltip>
    </div>
  );
}
